/**
 * WHO SEES AND WHO EDITS EACH ELEMENT — the web's half of element rules
 * (KPR-509; keepr-api docs/SCHEMA.md § Element grants; owner rulings e01–e09
 * on KPE-019, keepr-api docs/plans/element-grants.md § Web surfaces).
 *
 * Two readers live here:
 *
 * 1. THE CALLER'S OWN LEVEL on an element, as the API serves it on a card
 *    definition (KPR-505): every element of a collection card carries
 *    `access: 'see' | 'edit'` while the server's switch is on, and an editor
 *    (a manager, an admin) also gets the elements whose VALUES are hidden
 *    from them, stamped `access: 'hidden'` (a PATCH sends the whole list
 *    back, so leaving them out would delete them). An element hidden from a
 *    reader is simply not in the list. With the switch off nothing carries
 *    `access`, and every reader below answers as before: the API decides,
 *    and the web never infers a level the API did not send.
 *
 * 2. A RULE AS A MANAGER WRITES IT — one row per (collection, card, element
 *    id), `rules: [{ audience, level }]` plus `keeprOnly` — read and written
 *    by the Access dialog (components/cards/ElementAccessModal.js).
 */

import { normalizeId } from './ids';

// A card's page with its tab and the collection it was reached from — the
// shape utils/cards cardPagePath builds, written here rather than imported
// because utils/cards reads this module (withoutHiddenElements), and a cycle
// between the two would hand one of them a half-built module under jest.
const cardTabPath = (cardId, collectionId, tab) => {
	const params = new URLSearchParams();
	if (tab) params.set('tab', tab);
	if (collectionId) params.set('collectionId', collectionId);
	return `/cards/${cardId}?${params.toString()}`;
};

export const LEVELS = Object.freeze(['hidden', 'see', 'edit']);
export const LEVEL_LABELS = Object.freeze({ hidden: 'Hidden', see: 'See only', edit: 'Edit' });

/** The one sentence under, or beside, anything a rule holds to "see only". */
export const GRANT_READ_ONLY_REASON = 'Only some people can change this.';

/** The words History uses for a change the reader may not see (ruling e05). */
export const RESTRICTED_CHANGE_TEXT = 'A restricted field changed';

const isLevel = value => LEVELS.includes(value);

/** The caller's level on this element as the API served it, or null when it said nothing. */
export const accessOf = def => (isLevel(def?.access) ? def.access : null);

/** A value this person may read but not change (a rule holds them to "see only"). */
export const isSeeOnlyElement = def => accessOf(def) === 'see';

/**
 * An element whose VALUES are hidden from this person, served to them only
 * because they edit the card. Item surfaces leave it out: the values never
 * arrive, and an empty field would read as "not set".
 */
export const isHiddenFromMe = def => accessOf(def) === 'hidden';

/**
 * A list without the elements hidden from this person — the SAME array when
 * nothing is hidden, so a memo keyed on it does not churn (the common case:
 * the switch is off, or the card has no rule).
 */
export function withoutHiddenElements(defs) {
	if (!Array.isArray(defs)) return defs;
	return defs.some(isHiddenFromMe) ? defs.filter(def => !isHiddenFromMe(def)) : defs;
}

// ---- The columns of a repeating group (KPR-688) ----------------------------
//
// A group's columns carry `access` too, on the group's `options.columns` (a
// card definition) — the caller's level on the column, never more than on the
// group: a column inherits the group's rule and can only narrow it. A column
// hidden from a reader is not in the list; an editor gets every column, a
// hidden one stamped `access: 'hidden'`. The item form's entries (KPR-680)
// read these: a hidden column is left out, a see-only one is read-only.

/** The caller's level on one column of `group` (by name), or null when the API said nothing. */
export function columnAccessOf(group, columnName) {
	const columns = Array.isArray(group?.options?.columns) ? group.options.columns : (Array.isArray(group?.columns) ? group.columns : []);
	const column = columns.find(col => col?.name === columnName);
	return column ? accessOf(column) : null;
}

/** A column this person may read but not change. */
export const isSeeOnlyColumn = (group, columnName) => columnAccessOf(group, columnName) === 'see';

/**
 * The group with the columns hidden from this person left out — the SAME
 * object when none is (an editor's card carries them, stamped hidden).
 */
export function withoutHiddenColumns(group) {
	const columns = Array.isArray(group?.options?.columns) ? group.options.columns : null;
	if (!columns || !columns.some(isHiddenFromMe)) return group;
	return { ...group, options: { ...group.options, columns: columns.filter(col => !isHiddenFromMe(col)) } };
}

// ---- Rules, as the Access dialog reads and writes them --------------------

export const ROLE_AUDIENCES = Object.freeze(['manage', 'write', 'read']);

/** How each audience is named in the dialog: plain words, never "role". */
export const ROLE_LABELS = Object.freeze({
	manage: 'Managers',
	write: 'People who can edit',
	read: 'People who can view',
});
export const FOLLOWERS_LABEL = 'Followers';
export const PUBLIC_LABEL = 'Public form visitors';

/** One key per audience, for rows, React keys and duplicate checks. */
export function audienceKey(audience) {
	const type = audience?.type;
	if (type === 'role') return `role:${audience.role}`;
	if (type === 'group' || type === 'user') return `${type}:${normalizeId(audience.id) || ''}`;
	if (type === 'followers' || type === 'public') return type;
	return null;
}

/**
 * The audience as the PUT takes it. The GET hydrates a rule's audience (a
 * group's `name`, `kind` and `collection_id`, a person's `user`, `missing`);
 * the PUT refuses any key its type does not take (400 `invalid_audience`),
 * so a statement is always rebuilt from these few keys and never echoes the
 * hydrated shape back.
 */
export function wireAudience(audience) {
	const type = audience?.type;
	if (type === 'role') return { type, role: audience.role };
	if (type === 'group' || type === 'user') return { type, id: normalizeId(audience.id) };
	if (type === 'followers' || type === 'public') return { type };
	return null;
}

/**
 * The nearest row for `elementId` on a card ABOVE the last card of
 * `chainIds` (parent-first, as utils/cards cardChain gives it, ending with
 * the card itself): what governs its items when the card has no row of its
 * own. `{ grant, own: false }` or null.
 *
 * Every row that is not the card's own counts as "above", whether or not
 * the chain knows its card yet: the GET with `card_id` answers only the
 * card's own rows and its ancestors' (keepr-api § Element grants), and the
 * card page's chain waits on the card list — reading "no rule above" from a
 * chain that has not arrived (or failed) would show a parent's Hidden as
 * "Follows the card" and turn an emptied rule into a DELETE that brings the
 * parent's back (the KPR-509 review's finding 2). The chain only ORDERS
 * them: the nearest known ancestor wins, and a row whose card it does not
 * know yet comes after the known ones.
 */
export function aboveGrant(grants, chainIds, elementId) {
	const id = `${elementId || ''}`;
	if (!id || !Array.isArray(grants)) return null;
	const ids = (chainIds || []).map(normalizeId).filter(Boolean);
	const own = ids[ids.length - 1] || null;
	const rank = cardId => {
		const index = ids.indexOf(cardId);
		return index === -1 ? -1 : index;
	};
	let best = null;
	grants.forEach(grant => {
		const cardId = normalizeId(grant?.card_id);
		if (!cardId || cardId === own || `${grant?.elementId || ''}` !== id) return;
		if (!best || rank(cardId) > rank(normalizeId(best.card_id))) best = grant;
	});
	return best ? { grant: best, own: false } : null;
}

/**
 * The row that governs `elementId` for items of the LAST card of `chainIds`:
 * the card's own row (a row with no rules still shadows the ones above it),
 * else the nearest one above (aboveGrant) — the resolver's rule (keepr-api
 * § Element grants, "Descendant cards inherit"). `{ grant, own }` or null.
 */
export function governingGrant(grants, chainIds, elementId) {
	const id = `${elementId || ''}`;
	if (!id || !Array.isArray(grants)) return null;
	const ids = (chainIds || []).map(normalizeId).filter(Boolean);
	const own = ids[ids.length - 1];
	const row = own ? grants.find(grant => normalizeId(grant?.card_id) === own && `${grant?.elementId || ''}` === id) : null;
	if (row) return { grant: row, own: true };
	return aboveGrant(grants, ids, id);
}

// ---- What the collection above gives (KPR-623) ----------------------------

/** The public link's viewer, in the platform's words (never "share link"). */
export const SHARE_LABEL = 'Public link viewers';
export const KEYS_LABEL = 'Assistants, apps and API keys';

const LEVEL_ORDER = Object.freeze({ hidden: 0, see: 1, edit: 2 });

/**
 * What the collection above gives one element of one card (keepr-api §
 * What the collection above gives): `{ rules, keeprOnly, share }`, or
 * `{ unknown: true }` when keepr cannot work it out, or null when nothing
 * above rules it. `inherited` is the GET's list; null there means the API
 * said nothing, which the caller must not read as "nothing above".
 */
export function inheritedFor(inherited, cardId, elementId) {
	if (!Array.isArray(inherited)) return null;
	const card = normalizeId(cardId);
	const id = `${elementId || ''}`;
	if (!card || !id) return null;
	return inherited.find(entry => normalizeId(entry?.card_id) === card && `${entry?.elementId || ''}` === id) || null;
}

/** The level the collection above gives one of the five stated audiences, or null. */
export function aboveLevelFor(entry, audience) {
	if (!entry || entry.unknown || !Array.isArray(entry.rules)) return null;
	const type = audience?.type;
	if (type !== 'role' && type !== 'followers' && type !== 'public') return null;
	const key = audienceKey(audience);
	const found = entry.rules.find(rule => audienceKey(rule?.audience) === key);
	return isLevel(found?.level) ? found.level : null;
}

/**
 * What a level comes to for an audience, the way keepr-api's resolver reads
 * it: managers are never hidden, viewers and followers never edit.
 */
function effectiveLevel(audience, level) {
	if (!isLevel(level)) return null;
	if (audience?.type === 'role' && audience.role === 'manage' && level === 'hidden') return 'see';
	const viewOnly = (audience?.type === 'role' && audience.role === 'read') || audience?.type === 'followers';
	return viewOnly && level === 'edit' ? 'see' : level;
}

/**
 * Would this row give its audience more than the collection above does?
 * The server refuses such a save (409 `element_rule_widens`); the dialog
 * says so on the row before Save rather than after. A row with no level
 * follows the card — managers and writers edit, viewers see, the form edits
 * — and followers without a level follow the viewers' row (`rows`), as the
 * resolver reads them. Only the five stated audiences are judged: a person
 * or group row is the server's to judge (it walks who is in what).
 */
export function exceedsAbove(row, above, rows = []) {
	if (!isLevel(above)) return false;
	const audience = row?.audience;
	const type = audience?.type;
	if (type !== 'role' && type !== 'followers' && type !== 'public') return false;
	let mine = effectiveLevel(audience, row?.level);
	if (mine === null) {
		if (type === 'role') mine = audience.role === 'read' ? 'see' : 'edit';
		else if (type === 'public') mine = 'edit';
		else if (type === 'followers') {
			const viewers = (rows || []).find(other => other?.audience?.type === 'role' && other.audience.role === 'read');
			mine = effectiveLevel({ type: 'role', role: 'read' }, viewers?.level) || 'see';
		}
	}
	return LEVEL_ORDER[mine] > LEVEL_ORDER[effectiveLevel(audience, above)];
}

/** A level in words, as a row says what it comes to. */
export const levelWords = level => (level === 'hidden' ? 'Can’t see it' : level === 'see' ? 'Can see it, can’t change it' : 'Can see and change it');

/**
 * What a GROUP's rule gives the five stated audiences (KPR-688) — the
 * ceiling of each column's row: `{ rules: Map(audience key -> level),
 * keeprOnly }` from the group's governing row (governingGrant), or an empty
 * map when the group has none (it follows the card).
 */
export function groupLevelsOf(grant) {
	const rules = new Map();
	(Array.isArray(grant?.rules) ? grant.rules : []).forEach((rule) => {
		const key = audienceKey(rule?.audience);
		if (key && isLevel(rule?.level) && ['role:manage', 'role:write', 'role:read', 'followers', 'public'].includes(key)) rules.set(key, rule.level);
	});
	return { rules, keeprOnly: grant?.keeprOnly === true };
}

/**
 * The level the group gives one stated audience — its own rule, else the
 * card's access (managers and writers edit, viewers see, the form edits;
 * followers follow the viewers), as the resolver reads it — or null for a
 * person or a group (the server works those out).
 */
export function groupLevelFor(levels, audience) {
	if (!levels) return null;
	const key = audienceKey(audience);
	const own = key ? levels.rules.get(key) : undefined;
	if (audience?.type === 'role') {
		if (own) return effectiveLevel(audience, own);
		return audience.role === 'read' ? 'see' : 'edit';
	}
	if (audience?.type === 'followers') {
		if (own) return effectiveLevel(audience, own);
		const viewers = levels.rules.get('role:read');
		return viewers ? effectiveLevel({ type: 'role', role: 'read' }, viewers) : 'see';
	}
	if (audience?.type === 'public') return own || 'edit';
	return null;
}

/** A rule's audience no longer exists (a deleted group, a departed account): it matches nobody. */
export const isMissingAudience = audience => audience?.missing === true;

/**
 * The statement a PUT sends, from the dialog's rows: each row with a level,
 * in row order, rebuilt to the wire shape. Rows without a level ("follows
 * the card") and audiences that no longer exist are left out — the PUT
 * refuses the latter (`unknown_group` / `unknown_user`), and a rule for
 * nobody protects nothing.
 */
export function statementFromRows(rows, keeprOnly) {
	const seen = new Set();
	const rules = [];
	(rows || []).forEach(row => {
		if (!row || !isLevel(row.level) || isMissingAudience(row.audience)) return;
		const audience = wireAudience(row.audience);
		const key = audienceKey(audience);
		if (!audience || !key || seen.has(key)) return;
		seen.add(key);
		rules.push({ audience, level: row.level });
	});
	return { rules, keeprOnly: keeprOnly === true };
}

/** Two statements say the same thing (order aside), so Save has nothing to do. */
export function sameStatement(a, b) {
	const canon = s => JSON.stringify({
		keeprOnly: s?.keeprOnly === true,
		rules: [...(s?.rules || [])]
			.map(rule => [audienceKey(rule.audience), rule.level])
			.sort((x, y) => `${x[0]}`.localeCompare(`${y[0]}`)),
	});
	return canon(a) === canon(b);
}

/**
 * What one row comes to for the people it names, in words — the effective
 * level the plan's Risks §5 asks the dialog to show, so most-specific-wins
 * never surprises a manager. Mirrors keepr-api authz/elementLevels.js for the
 * ROW (not for a person, who may also be in a group or named): rules only
 * narrow (the cap by card access), managers are never hidden, viewers and
 * followers never change a value, and a group built from records only lowers.
 *
 * `rows` is the whole table, because followers without a rule of their own
 * get the viewers' (step 6).
 */
export function effectiveWords(row, rows = []) {
	const audience = row?.audience || {};
	const level = isLevel(row?.level) ? row.level : null;
	const says = value => (value === 'hidden' ? 'Can’t see it' : value === 'see' ? 'Can see it, can’t change it' : 'Can see and change it');
	if (audience.type === 'role') {
		if (audience.role === 'manage') return level ? says(level === 'hidden' ? 'see' : level) : `Follows the card: ${says('edit').toLowerCase()}`;
		if (audience.role === 'write') return level ? says(level) : `Follows the card: ${says('edit').toLowerCase()}`;
		if (audience.role === 'read') {
			if (!level) return `Follows the card: ${says('see').toLowerCase()}`;
			return level === 'edit' ? `${says('see')} (people who can view never change items)` : says(level);
		}
	}
	if (audience.type === 'followers') {
		if (level) return level === 'edit' ? `${says('see')} (followers never change items)` : says(level);
		const viewers = (rows || []).find(other => other?.audience?.type === 'role' && other.audience.role === 'read');
		const viewersLevel = isLevel(viewers?.level) ? viewers.level : null;
		return viewersLevel
			? `Same as people who can view: ${says(viewersLevel === 'edit' ? 'see' : viewersLevel).toLowerCase()}`
			: `Same as people who can view: ${says('see').toLowerCase()}`;
	}
	if (audience.type === 'public') {
		return level ? says(level) : 'Follows the form: asked when the form asks for it';
	}
	if (audience.type === 'group' || audience.type === 'user') {
		if (isMissingAudience(audience)) return 'No longer exists. Left out when you save.';
		if (!level) return 'Choose a level';
		if (audience.type === 'group' && audience.kind === 'dynamic') {
			return `At most: ${says(level).toLowerCase()} (a group built from items can only narrow)`;
		}
		return level === 'hidden' ? says(level) : `${says(level)}, within their own access`;
	}
	return '';
}

// ---- The conflicts a rule save is refused for (KPR-508, R1–R9) ------------

/** What each kind of conflicting setup is called, in the product's words. */
const CONFLICT_KIND_WORDS = Object.freeze({
	'card-title': 'The title of',
	formula: 'A calculated element on',
	'driven-element': 'A driven element on',
	'required-when': 'A “required when” rule on',
	'bound': 'A date bound on',
	required: 'A required element on',
	unique: 'A no-duplicates element on',
	automation: 'The automation',
	notification: 'The notification',
	'personal-notification': 'Someone’s own notification',
	'filtered-grant': 'Filtered access for',
	'collection-link': 'The collection link',
	'share-link': 'The public link',
	'dynamic-group': 'The group',
	'tag-rule': 'The automatic tag',
});

/**
 * Where a manager fixes one conflicting setup — a path in this app, or null
 * when there is nowhere to send them (someone's personal notification: never
 * named, never opened; a kind this build does not know yet).
 */
export function conflictLink(conflict, fallbackCollectionId = null) {
	const kind = conflict?.kind;
	const id = normalizeId(conflict?.id);
	const cid = normalizeId(conflict?.collection_id) || normalizeId(fallbackCollectionId);
	switch (kind) {
		case 'card-title':
			return id ? cardTabPath(id, cid, 'display') : null;
		case 'formula':
		case 'driven-element':
		case 'required-when':
		case 'bound':
		case 'required':
		case 'unique':
			return id ? cardTabPath(id, cid, 'elements') : null;
		case 'automation':
			return cid ? `/collections/${cid}/form?tab=automations` : null;
		case 'notification':
			return cid && id ? `/collections/${cid}/notifications/${id}/edit` : (cid ? `/collections/${cid}/form?tab=notifications` : null);
		case 'filtered-grant':
		case 'dynamic-group':
		case 'collection-link':
		case 'share-link':
			return cid ? `/collections/${cid}/form?tab=sharing` : null;
		case 'tag-rule':
			return id ? `/tags/${id}` : null;
		default:
			return null;
	}
}

/** One conflict as a sentence's parts: `{ lead, name, tail }` (the name is the link text). */
export function conflictParts(conflict) {
	const lead = CONFLICT_KIND_WORDS[conflict?.kind] || 'A setup';
	const name = `${conflict?.name || ''}`.trim();
	const element = `${conflict?.element || ''}`.trim();
	const dependent = `${conflict?.dependent || ''}`.trim();
	let tail = element ? ` reads ${element}` : '';
	if (dependent && dependent !== element) tail += ` into ${dependent}`;
	return { lead, name, tail };
}

/** The conflicts of a 409 `element_rule_conflicts` (or a 400 `element_restricted`), or null. */
export function conflictsOf(error) {
	const data = error?.response?.data;
	if (!data || (data.code !== 'element_rule_conflicts' && data.code !== 'element_restricted')) return null;
	return Array.isArray(data.conflicts) ? data.conflicts : [];
}
